import { motion } from 'framer-motion';
import { Ship, Package, Globe, Award, TrendingUp, CheckCircle } from 'lucide-react';
// import ExportCountriesMap from '../components/ExportCountriesMap';

const Export = () => {
  const products = [
    {
      title: 'Coconut Fibre',
      description: 'Premium quality coir fibre for mattress, upholstery, and agricultural applications.',
      image: 'https://images.pexels.com/photos/27596374/pexels-photo-27596374.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      features: ['100% Natural', 'High Tensile Strength', 'Moisture Resistant', 'Eco-Friendly']
    },
    {
      title: 'Coco Peat',
      description: 'High-grade growing medium for horticulture, hydroponics, and soil conditioning.',
      image: 'https://images.pexels.com/photos/27596374/pexels-photo-27596374.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      features: ['Low EC & pH Balanced', 'High Water Retention', 'Biodegradable', 'Bulk Supply']
    }
  ];

  const certifications = [
    { name: 'ISO 9001:2015', description: 'Quality Management' },
    { name: 'APEDA Registered', description: 'Agricultural Export Authority' },
    { name: 'Phytosanitary Certificate', description: 'Plant Health Compliance' },
    { name: 'Fumigation Certificate', description: 'Container Treatment' }
  ];

  const logistics = [
    { icon: Ship, title: '20ft & 40ft Containers', description: 'Flexible shipment sizes' },
    { icon: Package, title: 'Custom Packaging', description: 'Compression & baling services' },
    { icon: Globe, title: '25+ Countries', description: 'Global shipping network' },
    { icon: TrendingUp, title: 'Volume Pricing', description: 'Competitive rates for bulk orders' }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="export-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="export-hero">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Export Division</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6">
              Global Trade.<br />
              Sustainable Products.
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-muted-foreground max-w-3xl">
              We export premium coconut fibre and coco peat to international buyers across 25+ countries, 
              backed by world-class logistics and quality certifications.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products */}
      <section className="py-24 md:py-32" data-testid="export-products">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Our Products</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight">
              Natural. Sustainable. Premium Quality.
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {products.map((product, index) => (
              <motion.div
                key={product.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                data-testid={`export-product-${index}`}
              >
                <div className="bg-white border border-slate-200 overflow-hidden">
                  <div className="h-80">
                    <img 
                      src={product.image} 
                      alt={product.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-8">
                    <h3 className="text-2xl font-medium mb-3">{product.title}</h3>
                    <p className="text-muted-foreground mb-6">{product.description}</p>
                    <div className="grid grid-cols-2 gap-3">
                      {product.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-bronze-500 flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Logistics Capability */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="logistics-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Logistics & Shipping</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight">
              Seamless Global Delivery
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {logistics.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-slate-200 p-6 text-center"
                data-testid={`logistics-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-50 mb-4">
                  <item.icon className="w-8 h-8 text-slate-900" />
                </div>
                <h3 className="text-lg font-medium mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Container Visuals */}
      <section className="py-24 md:py-32" data-testid="container-visuals">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Export Process</p>
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
                From Factory to Global Markets
              </h2>
              <p className="text-base leading-relaxed text-muted-foreground mb-6">
                Our streamlined export process ensures your products arrive on time, every time. We handle 
                everything from sourcing and quality inspection to documentation and final delivery.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-bronze-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
                  <div>
                    <h4 className="font-medium mb-1">Quality Sourcing</h4>
                    <p className="text-sm text-muted-foreground">Premium grade coconut products from trusted suppliers</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-bronze-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
                  <div>
                    <h4 className="font-medium mb-1">Processing & Packaging</h4>
                    <p className="text-sm text-muted-foreground">Compression, baling, and custom packaging</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-bronze-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
                  <div>
                    <h4 className="font-medium mb-1">Container Loading</h4>
                    <p className="text-sm text-muted-foreground">Efficient loading with maximum capacity utilization</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-bronze-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">4</div>
                  <div>
                    <h4 className="font-medium mb-1">Global Shipping</h4>
                    <p className="text-sm text-muted-foreground">Timely delivery to your destination port</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-96 lg:h-full"
            >
              <img 
                src="https://images.unsplash.com/photo-1769144256181-698b8f807066?q=80&w=2070&auto=format&fit=crop" 
                alt="Container Shipping"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Export Countries Map - Will be added */}
      {/* <ExportCountriesMap /> */}

      {/* Certifications */}
      <section className="py-24 md:py-32 bg-slate-900 text-white" data-testid="certifications-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-bronze-500 mb-4">Certifications & Standards</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight">
              Trusted. Certified. Compliant.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 border border-white/20 p-6 text-center"
                data-testid={`certification-${index}`}
              >
                <Award className="w-12 h-12 text-bronze-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">{cert.name}</h3>
                <p className="text-white/70 text-sm">{cert.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32" data-testid="export-cta">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
              Ready to Place Your Order?
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-muted-foreground mb-8 max-w-2xl mx-auto">
              Contact our export team for competitive pricing, shipping quotes, and product specifications.
            </p>
            <a 
              href="mailto:export@lmrimpex.com" 
              data-testid="export-inquiry-button"
              className="inline-flex items-center bg-slate-900 text-white hover:bg-slate-800 h-12 px-8 uppercase tracking-widest text-xs font-bold transition-all"
            >
              Send Inquiry
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Export;
